import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import MarketingLayout from './MarketingLayout';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';

type ServiceDetailConfig = {
  title: string;
  overview: string;
  process: string[];
  pricing: string[];
};

const SERVICE_DETAIL_MAP: Record<string, ServiceDetailConfig> = {
  'warehouse-3pl': {
    title: '海外仓/仓配托管',
    overview:
      '服务对象为本土或跨境卖家,提供订单履约(一件代发)-贴换标-备货中转-退货处理-仓储-检测翻新-简单加工等服务项目.\n按约定执行入库接收-分拣-上架-仓储-仓内增值处理-按需拣货-打包出库或销毁等业务流程.\n全系统记录,库存/订单/财务数据准确及时.\n支持线上渠道多平台及线下分销渠道业务稳定落地.',
    process: [
      '需求沟通与服务流程/价格约定',
      '提交资料,创建账号并开通系统权限',
      '开始系统操作,按需创建业务项目和子账号,完成项目基础配置',
      '发货入仓,可选择预报或者不预报(部分场景必须预报并粘贴入库标签)',
      '仓库接收货物后开始后续流程:分拣-上架-仓储-增值服务-按需出库...',
      '查看收费记录及账单,处理异议后支付账单',
    ],
    pricing: [
      '请联系本司客服按照项目具体需求报价',
    ],
  },
  'transit-consolidation': {
    title: '包裹转运/集运',
    overview:
      '服务对象为本土或跨境商品转运/集运客户，提供代收、验货、拍照、临时仓储、合箱分箱、转运出库等服务，提升转运效率与可视化。',
    process: [
      '创建转运需求：提交来源包裹信息与目的地要求。',
      '到仓签收与质检：完成包裹登记、外观检查、图片留档。',
      '集运作业处理：按指令执行合箱/分箱、补充包装与标签处理。',
      '发运与回传：完成出库发运并回传物流轨迹与操作记录。',
    ],
    pricing: [
      '基础处理费：按包裹件数或批次计费。',
      '仓储费：按超免费期后的占仓时长计费。',
      '操作费：按合箱、分箱、拍照、加固等附加动作计费。',
      '物流费用按承运渠道实时计价，支持多方案比选。',
    ],
  },
  'courier-logistics': {
    title: '快递物流',
    overview:
      '面向本土直发与跨境小包场景，提供多承运商渠道管理、线路匹配和时效成本平衡方案，支持海运与空运等方式组合。',
    process: [
      '渠道评估：根据目的地、时效、重量体积匹配物流方案。',
      '下单与面单处理：生成运单信息并完成标签与交接单。',
      '揽收与发运：对接承运商完成揽收及干线运输。',
      '轨迹追踪与异常处理：持续跟踪状态并处理延误/拒收/退件。',
    ],
    pricing: [
      '运费：按线路、计费重、服务级别结算。',
      '附加费：偏远地区、超长超重、燃油等按承运商规则计收。',
      '操作服务费：按批次或订单量计费（如标签处理、分拣复核）。',
      '可按客户月度发货规模提供阶梯报价。',
    ],
  },
  'procurement-sales': {
    title: '采购/销售代理',
    overview:
      '服务对象为本土或跨境买家/卖家，提供上游采购协同与下游销售渠道协同服务，帮助客户提升供销链路效率与资金周转能力。',
    process: [
      '业务需求评估：明确品类、采购频率、目标市场与合规要求。',
      '供应与渠道匹配：筛选供应商/分销渠道并完成合作条款确认。',
      '订单执行与交付：跟进采购、质检、发货及销售履约链路。',
      '对账与优化：按周期核算采购与销售数据并优化策略。',
    ],
    pricing: [
      '代理服务费：按订单金额比例或固定服务费计收。',
      '渠道服务费：按合作渠道规则结算。',
      '附加服务费：如质检、报关协调、售后处理等按项计费。',
      '具体收费标准根据业务模型和目标市场定制确认。',
    ],
  },
};

const ServiceDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const detail = useMemo(() => (slug ? SERVICE_DETAIL_MAP[slug] : null), [slug]);

  useDocumentMeta({
    title: detail?.title,
    description: detail?.overview.replace(/\n/g, ''),
  });

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-6xl px-6 py-14">
        {detail ? (
          <>
            <h1 className="text-3xl font-bold text-gray-900">{detail.title}</h1>
            <p className="mt-3 max-w-4xl whitespace-pre-line text-sm leading-7 text-gray-600">{detail.overview}</p>

            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-[#d9eaff] bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900">服务流程</h2>
                <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-7 text-gray-700">
                  {detail.process.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ol>
              </div>
              <div className="rounded-xl border border-[#d9eaff] bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900">收费标准</h2>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-7 text-gray-700">
                  {detail.pricing.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        ) : (
          <div className="rounded-xl border border-[#d9eaff] bg-white p-6 text-sm text-gray-600 shadow-sm">
            服务页面不存在或已下线。请返回
            <Link to="/" className="ml-1 text-blue-600 hover:text-blue-700 hover:underline">
              首页
            </Link>
            继续浏览。
          </div>
        )}
      </section>
    </MarketingLayout>
  );
};

export default ServiceDetailPage;
